import type { NativeStackNavigationProp } from "@react-navigation/native-stack";

import { Routes } from "../constants";
import type { RootStackParamList } from "../navigation/NavigationStackParams";
import type {
  CalendarDay,
  MealSlotSpecification,
  Organisation,
  OrganisationRecipeInspirationTagConfig,
  PlanSimpleRecipe,
  PlanSimpleRecipeRequest,
  SimpleRecipe,
} from "../services/backendTypes";
import logger from "../services/logger";

interface AddSimpleRecipeToPlannerParams {
  recipe: SimpleRecipe;
  mealSlot: MealSlotSpecification;
  currentCalendarDay: CalendarDay;
  planSimpleRecipe: (args: { planSimpleRecipeRequest: PlanSimpleRecipeRequest }) => Promise<PlanSimpleRecipe>;
  refetchCalendarDays: () => void;
  navigation: NativeStackNavigationProp<RootStackParamList, keyof RootStackParamList>;
}

export const addSimpleRecipeToPlanner = async ({
  recipe,
  mealSlot,
  currentCalendarDay,
  planSimpleRecipe,
  refetchCalendarDays,
  navigation,
}: AddSimpleRecipeToPlannerParams): Promise<void> => {
  try {
    // Use recipe's servings count
    const planSimpleRecipeRequest: PlanSimpleRecipeRequest = {
      simple_recipe_id: recipe.id,
      calendar_day_id: currentCalendarDay.id!,
      meal_slot_id: mealSlot.id,
      meal_servings: recipe.servings,
    };

    await planSimpleRecipe({ planSimpleRecipeRequest });
    refetchCalendarDays();

    // Navigate to DiaryViewScreen instead of going back
    navigation.navigate(Routes.BottomNavigationTab, { screen: Routes.DiaryViewScreen });
  } catch (error) {
    logger.error("Failed to plan SimpleRecipe", error);
    throw error;
  }
};

export const getMealMomentFromSlot = (mealSlot: MealSlotSpecification): string => {
  // Convert meal type to user-friendly string
  const mealTypeMap: Record<string, string> = {
    BREAKFAST: "breakfast",
    MORNING_SNACK: "morning snack",
    LUNCH: "lunch",
    AFTERNOON_SNACK: "afternoon snack",
    DINNER: "dinner",
    EVENING_SNACK: "evening snack",
  };

  return mealTypeMap[mealSlot.meal_type] || "meal";
};

/**
 * Calculate total recipe time (prep + cook) rounded up to nearest 5
 * @param recipe - SimpleRecipe object
 * @returns Total time in minutes, minimum 5, rounded up to nearest 5
 */
export const getTotalRecipeTime = (recipe: SimpleRecipe): number => {
  const prepTime = recipe.prep_time_minutes || 0;
  const cookTime = recipe.cook_time_minutes || 0;
  const totalTime = prepTime + cookTime;

  // If total is 0 or less, return minimum of 5
  if (totalTime <= 0) return 5;

  // Round up to nearest multiple of 5
  return Math.ceil(totalTime / 5) * 5;
};

// Constants for recipe tag management
export const DEFAULT_TAG_DISPLAY_ORDER = 999;

// Type definition for grouped recipes
export type GroupedRecipes = {
  [tagName: string]: {
    tag: string;
    title: string;
    recipes: SimpleRecipe[];
  };
};

/**
 * Groups recipes by their custom tags
 * @param recipes - Array of SimpleRecipe objects
 * @returns Object with tag names as keys and grouped recipe data as values
 */
export const groupRecipesByTag = (recipes: SimpleRecipe[]): GroupedRecipes => {
  const taggedGroups: GroupedRecipes = {};

  recipes.forEach((recipe) => {
    recipe.custom_tags.forEach((tag) => {
      if (!taggedGroups[tag.name]) {
        taggedGroups[tag.name] = {
          tag: tag.name,
          title: tag.human_name,
          recipes: [],
        };
      }
      taggedGroups[tag.name]?.recipes.push(recipe);
    });
  });

  return taggedGroups;
};

/**
 * Filters and sorts recipe tags based on organisation configuration
 * @param organisation - Organisation object with display configuration
 * @param allTags - Array of all tag names
 * @param allRecipes - Array of all recipes (needed to map tag names to tag objects)
 * @returns Filtered and sorted array of tag names
 */
export const getFilteredAndSortedTags = (
  organisation: Organisation | undefined,
  allTags: string[],
  allRecipes: SimpleRecipe[]
): string[] => {
  if (!organisation?.displayed_recipe_tags || organisation.displayed_recipe_tags.length === 0) {
    // No configuration - return all tags
    return allTags;
  }

  // Create a map of tag IDs to display orders
  const tagOrderMap = new Map<number, number>();
  organisation.displayed_recipe_tags.forEach((config: OrganisationRecipeInspirationTagConfig) => {
    if (config.tag && config.display_order !== undefined) {
      tagOrderMap.set(config.tag.id, config.display_order);
    }
  });

  // Filter to only configured tags and sort by display_order
  return allTags
    .filter((tagName) => {
      const tag = allRecipes.flatMap((r) => r.custom_tags).find((t) => t.name === tagName);
      return tag && tagOrderMap.has(tag.id);
    })
    .sort((a, b) => {
      const tagA = allRecipes.flatMap((r) => r.custom_tags).find((t) => t.name === a);
      const tagB = allRecipes.flatMap((r) => r.custom_tags).find((t) => t.name === b);
      const orderA = tagA ? tagOrderMap.get(tagA.id) ?? DEFAULT_TAG_DISPLAY_ORDER : DEFAULT_TAG_DISPLAY_ORDER;
      const orderB = tagB ? tagOrderMap.get(tagB.id) ?? DEFAULT_TAG_DISPLAY_ORDER : DEFAULT_TAG_DISPLAY_ORDER;
      return orderA - orderB;
    });
};
